import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import {
  getAccountDisplayName,
  getAccountPlatformType,
} from "../../../entities/accounts";
import {
  replaceAccount,
  selectAccountById,
} from "../../../entities/accounts/table";
import { AccountType } from "../../../shared/types/accounts";
import { reconnectFacebookAccount } from "../../../api/social-account-connect/facebook";
import { reconnectInstagramAccount } from "../../../api/social-account-connect/instagram";
import {
  getUserFriendlyErrorMessage,
  UserFriendlyError,
} from "../../../api/UserFriendlyError";
import { getPinterestRedirect } from "../../../api/social-account-connect/pinterest";
import { getGmbRedirect } from "../../../api/social-account-connect/google";
import { getYtRedirect } from "../../../api/social-account-connect/youtube";
import { getTwitterRedirect } from "../../../api/social-account-connect/twitter";
import { getLinkedInRedirect } from "../../../api/social-account-connect/linkedin";
import { getTikTokRedirect } from "../../../api/social-account-connect/tiktok";
import { ONLY_MANUAL_PLATFORMS } from "../../../constants/Platforms";
import { reconnectManualAccount } from "../../../api/accounts";
import { PlatformNameToPlatformTypeMap } from "../../../features/post/format-post";
import { redirectToThreadsAuthPage } from "../../../api/social-account-connect/threads/trigger-auth-redirect";
import { ReduxRootState } from "../../../types/redux-store";
import { queryClient } from "../../../api/query-client";
import { metaAdsSettingsQueryKey } from "../../../pages/profile-settings/meta-ads-settings/meta-ads-settings.shared";
import { boostKeys } from "../../../features/boost/hooks/use-boost";
import { refreshInstagramCampaignStatus } from "../../../features/instagram-campaign/refresh-campaign-status";
import {
  updateLostPublishing,
  warnPublishingLost,
} from "../../../features/instagram-campaign/publishing-lost";
import { demoAccountReconnectNote } from "./components/demo-account-reconnect-note";
import { noteFacebookLimitedGrant } from "../../../features/social-account-connect/facebook-limited-grant-note";

export interface ReconnectAccountProps {
  account: AccountType;
  isPausedAccount?: boolean;
  otherAccountsDisabled?: boolean;
}

export type ReconnectAccountState = {
  pending: boolean;
};

const reconnectMethods: Record<
  string,
  (acc: AccountType) => Promise<AccountType | null>
> = {
  FB: reconnectFacebookAccount,
  IG: reconnectInstagramAccount,
  PI: async (_: AccountType) => {
    window.location.href = await getPinterestRedirect(true);
    return null;
  },
  GMB: async (account: AccountType) => {
    window.location.href = await getGmbRedirect(true, account._id);
    return null;
  },
  YT: async (account: AccountType) => {
    window.location.href = await getYtRedirect(true, account._id);
    return null;
  },
  TW: async (account: AccountType) => {
    window.location.href = await getTwitterRedirect(account.login, true);
    return null;
  },
  TT: async (account: AccountType) => {
    window.location.href = await getTikTokRedirect(
      true,
      account.login,
      account._id,
      account.ttData?.is_business_account !== false
    );
    return null;
  },
  TH: async (account: AccountType) => {
    window.location.href = await redirectToThreadsAuthPage({
      isReconnect: true,
      skedAccountId: account._id,
    });
    return null;
  },
  LI: async (account: AccountType) => {
    window.location.href = await getLinkedInRedirect(true, account._id);
    return null;
  },
};

const featureKey = "reconnectAccount" as const;

const initialState: ReconnectAccountState = {
  pending: false,
};

function getFeatureState(state: ReduxRootState): ReconnectAccountState {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: ReconnectAccountState;
      }
    )[featureKey] ?? initialState
  );
}

export function isReconnectSupported(account: AccountType): boolean {
  return Boolean(reconnectMethods[getAccountPlatformType(account)]);
}

export const reconnectAccountFx = createAsyncThunk(
  "reconnectAccount/run",
  async (
    { account, isPausedAccount, otherAccountsDisabled }: ReconnectAccountProps,
    thunkApi
  ) => {
    if (account.isMarketingDemo) {
      demoAccountReconnectNote.open(thunkApi.dispatch);
      return null;
    }
    try {
      const accountType = getAccountPlatformType(account);
      const isTTAccount = accountType === PlatformNameToPlatformTypeMap.tiktok;
      const isAutoPostTT = isTTAccount && account.platformId;

      ONLY_MANUAL_PLATFORMS.forEach((platformType) => {
        reconnectMethods[platformType] = reconnectManualAccount;
      });

      let method = reconnectMethods[accountType];

      if (
        (isPausedAccount && !isAutoPostTT) ||
        (otherAccountsDisabled && isTTAccount && !account.platformId)
      ) {
        method = reconnectManualAccount;
      }

      if (!method) {
        throw new UserFriendlyError(
          `Reconnecting is not supported for ${getAccountPlatformType(
            account
          )} accounts`
        );
      }

      /*
       * SKE-4807. The row as it stood BEFORE the update, read from the store
       * rather than the argument, so the comparison below is against what the
       * workspace actually held when the login began.
       */
      const accountBefore =
        selectAccountById(thunkApi.getState() as ReduxRootState, account._id) ??
        account;

      // SKE-4806. Instagram asks Facebook for the scopes the row it is given
      // holds, and the argument can be a stale copy, so it gets the store's.
      const updatedAccount = await method(
        accountType === "IG" ? accountBefore : account
      );

      if (updatedAccount) {
        thunkApi.dispatch(replaceAccount(updatedAccount));
        if (["FB", "IG"].includes(getAccountPlatformType(updatedAccount))) {
          void queryClient.invalidateQueries({
            queryKey: metaAdsSettingsQueryKey,
          });
          void queryClient.invalidateQueries({ queryKey: boostKeys.all });
        }
        if (getAccountPlatformType(updatedAccount) === "IG") {
          // SKE-4790, same reason as the insights reconnect path.
          refreshInstagramCampaignStatus();
        }
        if (
          getAccountPlatformType(updatedAccount) === "IG" &&
          updateLostPublishing(accountBefore, updatedAccount)
        ) {
          warnPublishingLost(getAccountDisplayName(updatedAccount));
        } else {
          toast.success(
            `Great! You have successfully reconnected ${getAccountDisplayName(
              updatedAccount
            )}. You might need to refresh the page to see any changes.`
          );
        }
        if (
          (getAccountPlatformType(updatedAccount) === "FB" ||
            getAccountPlatformType(updatedAccount) === "IG") &&
          updatedAccount.metaAdsCapabilities?.boosting === false
        ) {
          toast.info(
            "Meta Ads reporting is connected, but boosting is still unavailable. Reconnect again and approve the permission to create and manage ads."
          );
        } else if (
          (getAccountPlatformType(updatedAccount) === "FB" ||
            getAccountPlatformType(updatedAccount) === "IG") &&
          updatedAccount.metaAdsCapabilities?.boosting === true
        ) {
          toast.info("Meta Ads boosting is now enabled for this account.");
        }
        if (getAccountPlatformType(updatedAccount) === "FB") {
          noteFacebookLimitedGrant(updatedAccount);
        }
      }

      return updatedAccount;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

const reconnectAccountSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(reconnectAccountFx.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(reconnectAccountFx.fulfilled, (state) => {
      state.pending = false;
    });
    builder.addCase(reconnectAccountFx.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const reconnectAccountReducer = reconnectAccountSlice.reducer;

export const selectReconnectAccountPending = createSelector(
  getFeatureState,
  (reconnectAccountState) => reconnectAccountState.pending
);
